import { Dispatch, SetStateAction } from "react";
import { Dayjs } from "dayjs";
import { SaleDepositReviewStatusFilter } from "../../types/sale-deposit.type";
import { useUserRole } from "../../utils/hooks/useUserRole";
import DateRangePicker from "../common/DateRangePicker";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import FilterField, {
  FILTER_ROW_BOTTOM_SPACING,
  FLOATING_HELPER_TEXT_SX,
} from "./FilterField";
import {
  UNFILTERED_OPTION,
  useBranchRouteOptions,
} from "./useBranchRouteOptions";
import dayjs from "dayjs";
import "dayjs/locale/th";

dayjs.locale("th");

// สถานะ (review state) options — value "all" is skipped by the service. The three stages are
// ordered and a deposit sits in exactly one of them, so "BO ตรวจแล้ว" means verified by the
// branch and still waiting on head office.
export const DEPOSIT_STATUS_FILTER: {
  label: string;
  value: SaleDepositReviewStatusFilter;
}[] = [
  { label: "ทั้งหมด", value: "all" },
  { label: "ยังไม่ตรวจ", value: "not_reviewed" },
  { label: "BO ตรวจแล้ว", value: "bo_reviewed" },
  { label: "HO ตรวจแล้ว", value: "ho_reviewed" },
];

/** Guards the สถานะ value carried in the query string, which anyone may hand-edit. */
export const isReviewStatusFilter = (
  value: string | null,
): value is SaleDepositReviewStatusFilter =>
  DEPOSIT_STATUS_FILTER.some((option) => option.value === value);

interface SaleDepositFilterProps {
  dateFrom: Dayjs;
  dateTo: Dayjs;
  onChangeDateRange: (from: Dayjs, to: Dayjs) => void;
  selectedBranch: string;
  setSelectedBranch: Dispatch<SetStateAction<string>>;
  selectedRoute: string;
  setSelectedRoute: Dispatch<SetStateAction<string>>;
  status: SaleDepositReviewStatusFilter;
  setStatus: Dispatch<SetStateAction<SaleDepositReviewStatusFilter>>;
  /**
   * On the branch-day drill-down the สาขา and the day come from the URL. Their pickers would
   * offer to contradict the address bar, so they are dropped and only the filters that narrow
   * WITHIN the branch-day are left.
   */
  isBranchDayPinned?: boolean;
}

export default function SaleDepositFilter({
  dateFrom,
  dateTo,
  onChangeDateRange,
  selectedBranch,
  setSelectedBranch,
  selectedRoute,
  setSelectedRoute,
  status,
  setStatus,
  isBranchDayPinned = false,
}: SaleDepositFilterProps) {
  const { isHeadOfficeAccounting, isMaster } = useUserRole();
  // HO cohort selects a branch first, then its routes; others are token-scoped.
  const canSelectBranch = Boolean(isHeadOfficeAccounting || isMaster);
  const { branches, routeOptions, isRouteDisabled } = useBranchRouteOptions(
    canSelectBranch,
    selectedBranch,
  );

  const handleChangeBranch = (branchCode: string) => {
    setSelectedBranch(branchCode);
    setSelectedRoute(UNFILTERED_OPTION);
  };

  return (
    <Grid container spacing={2} mb={FILTER_ROW_BOTTOM_SPACING}>
      {!isBranchDayPinned && (
        <FilterField label="วันที่">
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={onChangeDateRange}
          />
        </FilterField>
      )}
      {canSelectBranch && !isBranchDayPinned && (
        <FilterField label="สาขา">
          <TextField
            select
            size="small"
            fullWidth
            value={selectedBranch}
            onChange={(e) => handleChangeBranch(e.target.value)}
          >
            <MenuItem value={UNFILTERED_OPTION}>ทั้งหมด</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.branch_code}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}
      <FilterField label="สายส่ง">
        <TextField
          select
          size="small"
          fullWidth
          disabled={isRouteDisabled}
          value={selectedRoute}
          onChange={(e) => setSelectedRoute(e.target.value)}
          helperText={isRouteDisabled ? "เลือกสาขาก่อน" : undefined}
          sx={FLOATING_HELPER_TEXT_SX}
        >
          <MenuItem value={UNFILTERED_OPTION}>ทั้งหมด</MenuItem>
          {routeOptions.map((route) => (
            <MenuItem key={route.route_code} value={route.route_code}>
              {route.route_code} - {route.route_name}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="สถานะ">
        <TextField
          select
          size="small"
          fullWidth
          value={status}
          onChange={(e) =>
            setStatus(e.target.value as SaleDepositReviewStatusFilter)
          }
        >
          {DEPOSIT_STATUS_FILTER.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
    </Grid>
  );
}
